import React, { FC, JSX } from 'react';

import {
	EvFilters,
	EvSection,
	IFilterDefinition,
	IFilterValues,
	TABLE_FILTERS_SR_SUMMARY,
	TFilterOptions
} from '@evinced-private/ui-common';

import { IFilterOptions } from './DataFilterHelper';

import './DataFilter.scss';

type TFilterTypes = 'text' | 'select';

interface IScanDataFilterProps {
	filterOptions: TFilterOptions;
	filterValues: IFilterOptions;
	onApply: (filterValues: IFilterOptions) => void;
	textSearchEnabled?: boolean;
	severitiesFilterEnabled?: boolean;
	typesFilterEnabled?: boolean;
	tagsFilterEnabled?: boolean;
	hideLabel?: boolean;
}

const ScanDataFilter: FC<IScanDataFilterProps> = ({
	filterOptions,
	filterValues,
	onApply,
	textSearchEnabled,
	severitiesFilterEnabled = true,
	typesFilterEnabled = true,
	tagsFilterEnabled = true,
	hideLabel
}: IScanDataFilterProps) => {
	const initialValues: IFilterValues = {
		text: { searchValue: filterValues?.searchValue },
		select: {
			components: filterValues?.components || [],
			severities: filterValues?.severities || [],
			types: filterValues?.types || [],
			tags: filterValues?.tags || []
		}
	};

	const handleApply = (appliedFilters: IFilterValues): void => {
		const flatFilters = {
			...appliedFilters.text,
			...appliedFilters.select
		};
		onApply(flatFilters);
	};

	const filterDefinitions: IFilterDefinition[] = [
		...(textSearchEnabled
			? [
					{
						id: 'searchValue',
						label: 'Search',
						filterType: 'text' as TFilterTypes,
						defaultValue: ''
					}
				]
			: []),

		...(severitiesFilterEnabled
			? [
					{
						id: 'severities',
						label: 'Select Severity',
						filterType: 'select' as TFilterTypes,
						defaultValue: [],
						componentProps: {
							id: 'severities',
							label: 'Severity',
							isMultiple: true,
							placeholder: 'Select severity',
							listboxWidth: '160px',
							allSelectedPlaceholder: 'All severities',
							someItemsSelectedText: (count: number) => `Severity (${count})`
						}
					}
				]
			: []),
		...(typesFilterEnabled
			? [
					{
						id: 'types',
						label: 'Select Type',
						filterType: 'select' as TFilterTypes,
						defaultValue: [],
						componentProps: {
							id: 'types',
							label: 'Type',
							placeholder: 'Select type',
							isMultiple: true,
							listboxWidth: '160px',
							allSelectedPlaceholder: 'All issue types',
							someItemsSelectedText: (count: number) => `Type (${count})`
						}
					}
				]
			: []),
		...(tagsFilterEnabled
			? [
					{
						id: 'tags',
						label: 'Select Level',
						filterType: 'select' as TFilterTypes,
						defaultValue: [],
						componentProps: {
							id: 'tags',
							label: 'Level',
							placeholder: 'Select level',
							isMultiple: true,
							listboxWidth: '160px',
							allSelectedPlaceholder: 'Conformance Level',
							someItemsSelectedText: (count: number) => `Conformance Level (${count})`
						}
					}
				]
			: [])
	];

	const renderSrSummary = (): JSX.Element => {
		const filteredComponents = filterValues?.components?.length || 0;
		const filteredIssueTypes = filterValues?.types?.length || 0;
		const filteredSeverities = filterValues?.severities?.length || 0;
		const filteredTags = filterValues?.tags?.length || 0;
		const allComponents = filterOptions?.components?.length || 0;
		const allIssueTypes = filterOptions?.types?.length || 0;
		const allSeverities = filterOptions?.severities.length || 0;
		const allTags = filterOptions?.tags?.length || 0;

		const componentsText =
			filteredComponents === allComponents || !filterValues?.components
				? 'all'
				: filteredComponents;
		const typesText =
			filteredIssueTypes === allIssueTypes || !filterValues?.types ? 'all' : filteredIssueTypes;
		const severitiesText =
			filteredSeverities === allSeverities || !filterValues?.severities
				? 'all'
				: filteredSeverities;
		const tagsText = filteredTags === allTags || !filterValues?.tags ? 'all' : filteredTags;

		return (
			<div id={TABLE_FILTERS_SR_SUMMARY} style={{ display: 'none' }}>
				Table Filter Results:
				{`components: ${componentsText},`}
				{`issue types: ${typesText},`}
				{`severity levels: ${severitiesText}`}
				{`conformance levels: ${tagsText}`}
			</div>
		);
	};

	return (
		<EvSection
			className="scan-data-filter"
			ariaLabel="Table filters"
			ariaDescribedby={TABLE_FILTERS_SR_SUMMARY}
			ariaLive="polite"
		>
			{!hideLabel && <label>Filter results</label>}
			<EvFilters
				className="data-filters"
				onApply={handleApply}
				onReset={handleApply}
				initialValues={initialValues}
				filterOptions={filterOptions}
				isFilterOptionsLoaded={true}
				filterDefinitions={filterDefinitions}
			/>
			{renderSrSummary()}
		</EvSection>
	);
};

export { ScanDataFilter };
